import { computed, h, defineComponent, ref } from 'vue';
import { dropdownField } from '@/ui/components/form';
import { t, toUnixTimestamp } from '@/ui/i18n/i18n';
import Chart from '@/ui/components/chart/Chart.vue';
import { MeasurementList, MeasurementRecord } from '@/entities/measurement';
import { DEFAULT_GRANULARITY, Sensor } from '@/entities/sensor';

interface MeasurementsChartProps {
  measurements: MeasurementList;
  start: Date;
  end: Date;
  sensor: Sensor;
}

enum CHART_METRICS {
  TEMPERATURE = 'temperature',
  HUMIDITY = 'humidity',
}

export const SensorMeasurementsChart = defineComponent<MeasurementsChartProps>({
  name: 'SensorMeasurementsChart',
  props: ['measurements', 'start', 'end', 'sensor'],
  setup(props) {
    const chartMetric = ref(CHART_METRICS.TEMPERATURE);

    const chartData = computed(() => {
      const measurementsList = props.measurements.data;
      if (measurementsList.length === 0) {
        return null;
      }

      let format: string;
      let key: string;
      switch (chartMetric.value) {
        case CHART_METRICS.TEMPERATURE:
          format = 'metricTemperature';
          key = 't';
          break;
        case CHART_METRICS.HUMIDITY:
          format = 'metricHumidity';
          key = 'h';
          break;
        // no default
      }

      const measurementLabel = (measurement: MeasurementRecord) => toUnixTimestamp(measurement['@'].getTime());

      return {
        labels: measurementsList.map(measurementLabel),
        series: [measurementsList.map((measurement: MeasurementRecord) => ({
          x: measurementLabel(measurement),
          y: key === 't' ? measurement.t : measurement.h,
        }))],
        format,
      };
    });

    return function render() {
      return h('div', {}, [
        dropdownField(
          {
            name: 'chartMetric',
            label: '',
            value: chartMetric.value,
            placeholder: '',
            required: false,
            options: [
              {
                label: t('sensors.temperature'),
                value: CHART_METRICS.TEMPERATURE,
              }, {
                label: t('sensors.humidity'),
                value: CHART_METRICS.HUMIDITY,
              },
            ],
            errors: [],
            disabled: false,
          },

          value => {
            chartMetric.value = value;
          },
        ),
        h(Chart, {
          props: {
            chartData: chartData.value,
            chartStartsAt: toUnixTimestamp(props.start.getTime()),
            chartEndsAt: toUnixTimestamp(props.end.getTime()),
            granularity: props.sensor.granularity ?? DEFAULT_GRANULARITY,
          },
        }),
      ]);
    };
  },
});
